import { css } from "@emotion/react"
import styled from "@emotion/styled"
import { useGradesFromContext } from "app/core/providers/SystemInfoProvider"
import parseStringTime from "app/rehearsal/utils/parseStringTime"
import type {
  File,
  Grade,
  HeadSubject,
  HeadSubjectGroup,
  Lesson,
  ParticipationIcon,
  SubSubject,
} from "db/client"
import { ComponentProps, forwardRef, useMemo } from "react"
import { DARK_BLUE_COLOR, LIGHT_GREY_COLOR, PX12, PX16 } from "theme/consts"
import Grades from "ui/Grades"
import Group from "ui/Group"
import Link from "ui/Link"
import Stack from "ui/Stack"
import Text from "ui/Text"
import { FileWithVariants } from "utils/files/types"
import LessonListItemTitle from "web/src/lessons/components/LessonListItemTitle"
import LessonPreview, {
  PreviewLink,
} from "web/src/lessons/components/LessonPreview"
import LessonSubTitle from "web/src/lessons/components/LessonSubTitle"
import ParticipationIcons from "web/src/lessons/components/ParticipationIcons"
import ShowLessonButton from "./ShowLessonButton"
import CardLabel from "ui/CardLabel"
import getContentSubjectColor from "app/content-subjects/getContentSubjectColor"

export const HIDE_IMAGE_BREAKPOINT = "650px"
export const SWAP_BUTTON_BREAKPOINT = "400px"

export type TParticipantsIcons = ParticipationIcon & {
  image: File | null
}

export type TLesson = Lesson & {
  grades: Grade[]
  // listImage: File | null
  cardImage: File | null
  previewVideo: FileWithVariants | null
  previewAudio: File | null
  radioSong: File | null
  rehearse: {
    video: FileWithVariants | null
  } | null
  subjects: (HeadSubjectGroup & {
    subjects: (HeadSubject & {
      subjects: SubSubject[]
      participationIcons: TParticipantsIcons[]
    })[]
  })[]
  learningLines?: {
    LearningLineLessonGroup: {
      learningLineContentPagePlanningLessonGroups: {
        cardLabel: string | null
      } | null
    }
  }[]
}

export const OuterRoot = styled.div`
  container-type: inline-size;
`

export const InnerRoot = styled.div`
  all: unset;
  display: grid;
  grid-template-columns: 1fr 200px;
  gap: 20px;
  text-decoration: none;
  color: ${DARK_BLUE_COLOR};
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  @container (max-width: ${SWAP_BUTTON_BREAKPOINT}) {
    display: flex;
    flex-direction: column;
  }
`

export const Root = forwardRef<
  HTMLDivElement,
  ComponentProps<typeof OuterRoot>
>(({ className, ...props }, ref) => {
  return (
    <OuterRoot className={className} ref={ref}>
      <InnerRoot {...props} />
    </OuterRoot>
  )
})

Root.displayName = "LessonListItem.Root"

export const RootWithBorder = styled(Root)`
  border-bottom: 2px solid ${LIGHT_GREY_COLOR};
  padding: 24px 55px 24px 8px;
`

export const Title = styled.h2`
  font-size: ${PX16};
  font-weight: bold;
  letter-spacing: 0.4px;
`

export const Content = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 16px;
`

export const TitleWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 6px;
  align-items: center;
`

type ImageRootProps = {
  size?: {
    width: string
    height: string
  }
}

export const ImageRoot = styled.div<ImageRootProps>`
  position: relative;
  ${(p) =>
    p.size
      ? css`
          width: ${p.size.width};
          height: ${p.size.height};
        `
      : css`
          width: 240px;
          min-height: 135px;
        `};
  border-radius: 8px;
  overflow: hidden;

  @container (max-width: ${HIDE_IMAGE_BREAKPOINT}) {
    display: none;
  }
`

type Props = {
  lesson: TLesson
  href: string
} & ComponentProps<typeof RootWithBorder>

const LessonListItem = ({ lesson, href, ...props }: Props) => {
  const grades = useGradesFromContext()

  return (
    <RootWithBorder {...props}>
      <Group gap="40px">
        {lesson.cardImage && (
          <PreviewLink href={href}>
            <LessonPreview
              previewId={`listitem-${lesson.id}`}
              lessonPreviewAnalyticsPagetype={"THEMA_LIJST"}
              isPlayVisible={
                !!lesson?.previewVideo ||
                !!lesson?.previewAudio ||
                !!lesson?.rehearse?.video ||
                !!lesson?.radioSong
              }
              image={lesson.cardImage}
              previewBegin={
                lesson?.previewBegin
                  ? parseStringTime(lesson.previewBegin)
                  : undefined
              }
              previewEnd={
                lesson?.previewEnd
                  ? parseStringTime(lesson.previewEnd)
                  : undefined
              }
              file={
                lesson?.previewVideo ||
                lesson?.previewAudio ||
                lesson?.rehearse?.video ||
                lesson?.radioSong
              }
            >
              {lesson.cardLabel && (
                <CardLabel
                  size={PX12}
                  color={getContentSubjectColor(lesson.contentSubject)}
                >
                  {lesson.cardLabel}
                </CardLabel>
              )}
            </LessonPreview>
          </PreviewLink>
        )}
        <Content>
          <Stack gap="8px">
            {lesson.displayTitle && (
              <Link href={href}>
                <LessonListItemTitle
                  title={lesson.displayTitle}
                  contentSubject={lesson.contentSubject}
                />
              </Link>
            )}
            <LessonSubTitle
              size={PX12}
              lesson={lesson}
              hasLearningLines={true}
            />
            <Grades
              allGrades={grades}
              activeGrades={lesson.grades}
              contentSubjectType={lesson.contentSubject}
            />
            <Text size={PX12}>{lesson.searchDescription}</Text>
          </Stack>
          <div>
            <ShowLessonButton lesson={lesson} />
          </div>
        </Content>
      </Group>
      <ParticipationIcons lesson={lesson} />
    </RootWithBorder>
  )
}

export default LessonListItem
